import { useCallback, useEffect, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { WalletRow } from '../../api/adminTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { AdminNotice, EmailLookupForm } from './adminShared'
import { describeAdminError, useEmailParam } from './adminHelpers'
const DEFAULT_EXCHANGE = 'binance'

/**
 * Equivalent of legacy `AdminUserWalletComponent` (route
 * `admin/users/wallet`, contract #31). USDT only (predates the USDC
 * fallback the user-facing wallet view has, per the contract's own note).
 */
export function AdminUserWalletPage() {
  const [email, setEmail] = useEmailParam()
  const [exchangeId, setExchangeId] = useState(DEFAULT_EXCHANGE)
  const [rows, setRows] = useState<WalletRow[] | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()

  const load = useCallback((targetEmail: string, ex: string) => {
    setRows(null)
    setFeedback(undefined)
    adminClient
      .wallet(ex, targetEmail)
      .then((result) => setRows(result))
      .catch((err) => setFeedback(describeAdminError(err)))
  }, [])

  useEffect(() => {
    if (email) load(email, exchangeId)
    else setRows(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const columns: Column<WalletRow>[] = [
    { key: 'coin', label: 'Coin', align: 'left', sortValue: (r) => r.coin },
    { key: 'balance', label: 'Balance', align: 'right', numeric: true, sortValue: (r) => r.balance, format: (_v, r) => formatNumberByCoin(r.balance, r.coin) },
    { key: 'mop', label: 'MOP', align: 'right', numeric: true, sortValue: (r) => r.mop, format: (_v, r) => formatNumberByCoin(r.mop, r.coin) },
    { key: 'mopCount', label: 'MOP count', align: 'right', numeric: true, sortValue: (r) => r.mopCount },
    { key: 'mopAmount', label: 'MOP amount (USDT)', align: 'right', numeric: true, sortValue: (r) => r.mopAmount, format: (v) => formatNumberByCoin(v as number, 'USDT') },
    { key: 'mopActualAmount', label: 'MOP value now (USDT)', align: 'right', numeric: true, sortValue: (r) => r.mopActualAmount, format: (v) => formatNumberByCoin(v as number, 'USDT') },
    { key: 'balanceActualUsdt', label: 'Balance value now (USDT)', align: 'right', numeric: true, sortValue: (r) => r.balanceActualUsdt, format: (v) => formatNumberByCoin(v as number, 'USDT') },
    {
      key: 'mopActualLossUsdtPercent',
      label: 'Unrealized (%)',
      align: 'right',
      numeric: true,
      sortValue: (r) => r.mopActualLossUsdtPercent,
      format: (v) => `${Number(v).toFixed(2)}%`,
      cellClass: (v) => (Number(v) < 0 ? 'portal-pnl-negative' : Number(v) > 0 ? 'portal-pnl-positive' : undefined),
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user wallet</h1>
      </div>
      <div className="portal-card">
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <form
          className="portal-toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            if (email) load(email, exchangeId)
          }}
        >
          <label htmlFor="wallet-exchange">Exchange</label>
          <input id="wallet-exchange" value={exchangeId} onChange={(e) => setExchangeId(e.target.value)} />
          <button type="submit" disabled={!email}>
            Reload
          </button>
        </form>
        <AdminNotice feedback={feedback} />
        {!email && <p className="portal-text-muted">Enter a user e-mail to view their wallet.</p>}
        {email && rows === null && !feedback && <p>Loading…</p>}
        {email && rows !== null && (
          <div className="portal-table-scroll">
            <Table columns={columns} rows={rows} rowKey={(r) => r.coin} />
          </div>
        )}
      </div>
    </div>
  )
}
